import {isUndefined, omit} from 'lodash';

import {createResourceTableUpdatedReducer} from '../../../helpers/models/resourceHelpers';
import {CommentModel, CommentStatusesEnum} from '../../../models/api/commentModel';
import {AppAction} from '../../appActions';
import {deleteFromResourceTable, ResourceTable} from '../../appTypes';
import {DataGroupsActionsEnum, DataUpdatedAction} from '../dataGroupsActions';
import {CommentsActionsEnum} from './commentsActions';

/*
 * Reducer.
 */

const commentsUpdatedReducer = createResourceTableUpdatedReducer(
  'comments',
  (comment) => comment.uid,
  mergeComment,
);

export function commentsReducer(
  state: ResourceTable<string, CommentModel>,
  action: AppAction,
): ResourceTable<string, CommentModel> {
  switch (action.type) {
    // Data Group.
    case DataGroupsActionsEnum.DATA_UPDATED:
      return updateData(state, action);

    case CommentsActionsEnum.SENDING_COMMENT_DELETED:
      // Since this deletion was initiated on the client, don't keep a tombstone. Either it doesn't exist on the server
      // and we'll never see it back anyway, or it does exist on the server and we'll want to re-introduce it.
      return deleteFromResourceTable(state, action.payload.commentUid, false);

    case CommentsActionsEnum.COMMENT_DELETED:
      return deleteFromResourceTable(state, action.payload.commentUid);

    default:
      return state;
  }
}

function updateData(state: ResourceTable<string, CommentModel>, action: DataUpdatedAction) {
  const deletedUids = action.payload.result.deletedCommentUids;
  const stateAfterDeletions =
    deletedUids && deletedUids.length > 0
      ? {
          ...state,
          byId: omit(state.byId, deletedUids),
          deletedIds: state.deletedIds.subtract(deletedUids),
        }
      : state;
  return commentsUpdatedReducer(stateAfterDeletions, action);
}

function mergeComment(existing: CommentModel | undefined, comment: CommentModel): CommentModel {
  // If no existing comment is found, nothing to merge.
  if (!existing || comment.status === CommentStatusesEnum.SENT) {
    return comment;
  }

  // Do not overwrite drafts that we were not able to save.
  if (
    existing &&
    existing.isSaving &&
    existing.status !== CommentStatusesEnum.SENT &&
    !comment.isSaving &&
    comment.status === CommentStatusesEnum.DRAFT &&
    existing.version === comment.version
  ) {
    return existing;
  }

  // Otherwise merge the optional properties.
  return {
    ...comment,
    teammateIds: comment.teammateIds || existing.teammateIds,
    topic: !isUndefined(comment.topic) ? comment.topic : existing.topic,
  };
}
